import Cookies from 'js-cookie';
import { DateTime } from 'luxon';

import { COMMONS, DATE_FORMAT } from 'globalConstants';
import { LUXON_RANGE_TYPE } from 'globalConstants/date';
import {
  FormatTimeStampType,
  GetTodayType,
  CompareDatesType,
  FormatDateTimeType,
  OperationInDateType,
  DatesDiffType,
  getFormattedDateFromIsoDateType,
} from 'types/dateHelpers';

const { YYYY_MM_DD, YYYY_MM_DD_HH_MM_A, MMM_dd_yyyy_HH_MM } = DATE_FORMAT;
const { ADD, SUBTRACT } = COMMONS;

// default time zone set to tenant's timezone
// export const DEFAULT_TIME_ZONE = 'America/Los_Angeles';
export const DEFAULT_TIME_ZONE = Cookies.get('tenant-timezone');

export function shouldUseLocalTimezone() {
  return Cookies.get('use-local-timezone') === 'true';
}

const GetToday: GetTodayType = (format = null, timeZone = DEFAULT_TIME_ZONE) => {
  if (timeZone) {
    if (format) {
      return DateTime.now().setZone(timeZone).toFormat(format);
    } else {
      return DateTime.now().setZone(timeZone);
    }
  } else {
    if (format) {
      return DateTime.now().toFormat(format);
    } else {
      return DateTime.now();
    }
  }
};

const FormatTimeStamp: FormatTimeStampType = (
  timeStamp,
  format = YYYY_MM_DD,
  timeZone = DEFAULT_TIME_ZONE
) => {
  if (!timeStamp) {
    return '';
  }
  if (timeZone && !shouldUseLocalTimezone()) {
    return DateTime.fromSeconds(timeStamp).setZone(timeZone).toFormat(format);
  } else {
    return DateTime.fromSeconds(timeStamp).toFormat(format);
  }
};

const getFormattedTime = (timestamp: number, format: string) => {
  return timestamp ? FormatTimeStamp(timestamp, format) : 'N/A';
};

const compareDates: CompareDatesType = (date1, date2, compBy = 'day', compType = 'EQUAL') => {
  switch (compType) {
    case 'EQUAL':
      return DateTime.fromISO(date2).startOf(compBy) == DateTime.fromISO(date1).startOf(compBy);
    case 'LESSTHAN':
      return DateTime.fromISO(date2).startOf(compBy) < DateTime.fromISO(date1).startOf(compBy);
    case 'GREATERTHAN':
      return DateTime.fromISO(date2).startOf(compBy) > DateTime.fromISO(date1).startOf(compBy);
    case 'LESSTHANEQUALTO':
      return DateTime.fromISO(date2).startOf(compBy) <= DateTime.fromISO(date1).startOf(compBy);
    case 'GREATERTHANEQUALTO':
      return DateTime.fromISO(date2).startOf(compBy) >= DateTime.fromISO(date1).startOf(compBy);
    default:
      return DateTime.fromISO(date2).startOf(compBy) == DateTime.fromISO(date1).startOf(compBy);
  }
};

const FormatDateTime: FormatDateTimeType = (dateTime, format = YYYY_MM_DD, timeZone) => {
  if (!dateTime) {
    return '';
  }

  let tempDate: number;
  if (typeof dateTime === 'string') {
    const jsDate = new Date(dateTime).toISOString();
    tempDate = DateTime.fromISO(jsDate).toSeconds();
  } else {
    tempDate = DateTime.fromISO(dateTime.toISOString()).toSeconds();
  }

  if (timeZone && !shouldUseLocalTimezone()) {
    return DateTime.fromSeconds(tempDate).setZone(timeZone).toFormat(format);
  } else {
    return DateTime.fromSeconds(tempDate).toFormat(format);
  }
};

const OperationInDate: OperationInDateType = (
  dateTime,
  duration = 0,
  optBy = 'days',
  format = YYYY_MM_DD,
  optType = ADD
) => {
  const timeZone = DEFAULT_TIME_ZONE;
  if (!dateTime) {
    return '';
  }

  const tempDate =
    typeof dateTime === 'string'
      ? DateTime.fromISO(dateTime)
      : DateTime.fromISO(dateTime.toISOString());

  switch (optType) {
    case ADD:
      return timeZone && !shouldUseLocalTimezone()
        ? tempDate
            .plus({ [optBy]: duration })
            .setZone(timeZone)
            .toFormat(format)
        : tempDate.plus({ [optBy]: duration }).toFormat(format);
    case SUBTRACT:
      return timeZone && !shouldUseLocalTimezone()
        ? tempDate
            .minus({ [optBy]: duration })
            .setZone(timeZone)
            .toFormat(format)
        : tempDate.minus({ [optBy]: duration }).toFormat(format);
    default:
      return '';
  }
};

const DatePickerOperationInDate: OperationInDateType = (
  dateTime,
  duration = 0,
  optBy = 'days',
  format = YYYY_MM_DD,
  optType = ADD
) => {
  const timeZone = DEFAULT_TIME_ZONE;
  if (!dateTime) return '';

  const tempDate =
    typeof dateTime === 'string'
      ? DateTime.fromISO(dateTime)
      : DateTime.fromISO(dateTime.toISOString());

  // Inclusive logic: if duration > 0, subtract 1 to include the start date
  const effectiveDuration = duration > 0 ? duration - 1 : duration;

  if (effectiveDuration === 0) {
    return timeZone && !shouldUseLocalTimezone()
      ? tempDate.setZone(timeZone).toFormat(format)
      : tempDate.toFormat(format);
  }

  const adjustedDate =
    optType === ADD
      ? tempDate.plus({ [optBy]: effectiveDuration })
      : optType === SUBTRACT
      ? tempDate.minus({ [optBy]: effectiveDuration })
      : null;

  return adjustedDate
    ? timeZone && !shouldUseLocalTimezone()
      ? adjustedDate.setZone(timeZone).toFormat(format)
      : adjustedDate.toFormat(format)
    : '';
};

const DatesDiff: DatesDiffType = (date1, date2, diffBy = 'days') => {
  const start =
    typeof date1 === 'string' ? DateTime.fromISO(date1) : DateTime.fromISO(date1.toISOString());
  const end =
    typeof date2 === 'string' ? DateTime.fromISO(date2) : DateTime.fromISO(date2.toISOString());
  const dateDiff = end.diff(start, diffBy);
  return dateDiff.toObject();
};

/**
 * @param {dateTime} Date | string | number
 * @returns {String} Short day format => "Fri, Jan 27"
 */
const FormatShortDate = (
  dateTime: Date | string | number,
  format?: string,
  useTimezone?: boolean
): string => {
  const renderFormat = format || 'ccc, LLL d';
  let outputUnixTimestamp = 0;

  if (typeof dateTime === 'number') {
    outputUnixTimestamp = dateTime;
  }

  if (dateTime instanceof Date) {
    const inputDateTime = DateTime.fromJSDate(dateTime);
    outputUnixTimestamp = inputDateTime.toSeconds();
  }

  if (typeof dateTime === 'string') {
    outputUnixTimestamp = DateTime.fromISO(formatISODate(dateTime)).toSeconds();
  }

  if (useTimezone && !shouldUseLocalTimezone()) {
    return DateTime.fromSeconds(outputUnixTimestamp)
      .setZone(DEFAULT_TIME_ZONE)
      .toFormat(renderFormat);
  } else {
    return DateTime.fromSeconds(outputUnixTimestamp).toFormat(renderFormat);
  }
};

const GetStartWeekDate = (date = DateTime.now().toISODate()): Date => {
  const ISODate = DateTime.fromISO(formatISODate(date));
  return ISODate.startOf('week').toJSDate();
};

const GetStartEndDayUnix = (
  date: Date,
  timeZone = DEFAULT_TIME_ZONE
): { startOfDayUnix: number; endOfDayUnix: number } => {
  const currentDate = DateTime.fromJSDate(date, {
    zone: shouldUseLocalTimezone() ? undefined : timeZone,
  });

  const startOfDay = currentDate.startOf('day');
  const startOfDayUnix = Math.floor(startOfDay.toSeconds());

  const endOfDay = currentDate.endOf('day');
  const endOfDayUnix = Math.floor(endOfDay.toSeconds());

  return {
    startOfDayUnix,
    endOfDayUnix,
  };
};

const GetStartEndDayUnixFromFormat = (
  date: string,
  fromString: string,
  timeZone = DEFAULT_TIME_ZONE
): { startOfDayUnix: number; endOfDayUnix: number } => {
  const currentDate = DateTime.fromFormat(date, fromString, {
    zone: shouldUseLocalTimezone() ? undefined : timeZone,
  });

  const startOfDay = currentDate.startOf('day');
  const startOfDayUnix = Math.floor(startOfDay.toSeconds());

  const endOfDay = currentDate.endOf('day');
  const endOfDayUnix = Math.floor(endOfDay.toSeconds());

  return {
    startOfDayUnix,
    endOfDayUnix,
  };
};

/**
 * Checks if the given timestamp is older than one month from the current date.
 *
 * @param timestamp - The timestamp to check.
 * @param timeZone - The timezone to consider. Defaults to DEFAULT_TIME_ZONE.
 * @returns An object containing the formatted date and a boolean indicating if it is older than one month.
 */
const isOlderThanOneMonth = (timestamp?: string | number, timeZone = DEFAULT_TIME_ZONE) => {
  if (!timestamp) return { isOlder: false, formattedDate: '-' };

  const parsedTimestamp = Number(timestamp);
  if (parsedTimestamp <= 0) return { isOlder: false, formattedDate: '-' };

  const lastSynced = DateTime.fromSeconds(parsedTimestamp).setZone(
    shouldUseLocalTimezone() ? 'local' : timeZone
  );
  const oneMonthOlder = DateTime.now()
    .setZone(shouldUseLocalTimezone() ? 'local' : timeZone)
    .minus({ months: 1 });

  const isOlder = lastSynced < oneMonthOlder;
  const formattedDate = lastSynced.toFormat(MMM_dd_yyyy_HH_MM);

  return { isOlder, formattedDate };
};
/* Returns the start and end timestamps of a given date period.
 *
 * @param {string | number} date - The date for which to get the start and end of the period.
 * @param {LUXON_RANGE_TYPE} rangeType - The type of date range accepts luxon range type.
 * @param {string} [timeZone=DEFAULT_TIME_ZONE] - The time zone to use for the date calculations.
 * @returns {{ startTimestamp: number; endTimestamp: number }} - An object containing the start and end timestamps of the period.
 *
 */
export const getStartAndEndUnix = (
  date: string | number,
  rangeType: LUXON_RANGE_TYPE = LUXON_RANGE_TYPE.MONTH,
  timeZone = DEFAULT_TIME_ZONE
): { startTimestamp: number; endTimestamp: number } => {
  const dateObj = DateTime.fromSeconds(Number(date), { zone: timeZone });

  const startTimestamp = dateObj.startOf(rangeType).toSeconds();
  const endTimestamp = Math.floor(dateObj.endOf(rangeType).toSeconds());

  return { startTimestamp: startTimestamp, endTimestamp: endTimestamp };
};

const formatTimeStamp = (timestamp: number, LocaleString = DateTime.DATE_MED): string =>
  DateTime.fromJSDate(new Date(timestamp * 1000))
    .setLocale('en')
    .toLocaleString(LocaleString);

/**
 * Converts a given date to a specified timezone.
 *
 * @param date - The date to be converted.
 * @param timezone - The target timezone. Defaults to DEFAULT_TIME_ZONE.
 * @returns The converted date in the specified timezone.
 */
const convertToTimezone = (date: Date, timeZone = DEFAULT_TIME_ZONE) => {
  return new Date(
    (typeof date === 'string' ? new Date(date) : date).toLocaleString('en-US', {
      timeZone: timeZone,
    })
  );
};

/**
 * Converts an ISO date string in the local timezone to a timestamp in UTC,
 * with an optional timezone override.
 *
 * @param isoDateString - The ISO date string to be converted.
 * @param timeZone - The timezone to override the local timezone. Defaults to DEFAULT_TIME_ZONE.
 * @returns The timestamp in UTC.
 */
const ISODateLocalToUTCTimestamp = (
  isoDateString: string,
  timeZone = DEFAULT_TIME_ZONE
): number => {
  return Math.floor(DateTime.fromISO(isoDateString, { zone: timeZone }).toUTC().toSeconds());
};

// ISO standard extended format
const formatISODate = (inputDateString: string) => {
  // Check if inputDateString is in 'MM/dd/yyyy' format
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(inputDateString)) {
    // Parse the input date string using the 'MM/dd/yyyy' format
    const inputDate = DateTime.fromFormat(inputDateString, 'MM/dd/yyyy');
    // Format the date using the 'yyyy-dd-MM' format
    const outputDateString = inputDate.toFormat('yyyy-dd-MM');
    return outputDateString; // "2023-15-02"
  }
  return inputDateString;
};

/**
 * Returns formatted date string in tenant timezone ( JS Date -> Date String )
 *
 * @param {Date} date The JS date instance
 * @param {string} format The return format
 * @return {string} formatted date string ( JS Date -> Date String timezoned )
 */
const JSDateToFormattedStringWTz = (
  date: Date,
  format: string = YYYY_MM_DD,
  timeZone: string = DEFAULT_TIME_ZONE || ''
) => {
  return DateTime.fromJSDate(date)
    .setZone(shouldUseLocalTimezone() ? 'local' : timeZone)
    .toFormat(format);
};

/**
 *
BELOW HELPERS DONOT MAKE USE OF TIMEZONE
THEY ARE IRRESPECTIVE OF TIMEZONES AND ARE USED TO FORMAT DATETIME ON FRONTEND ONLY
 */
/**
 * JS DATE HELPERS
 * JS DATE -> DATE STRING
 * JS DATE -> TIMESTAMP
 * JS DATE -> ISO FORMAT
 */

// JS DATE -> STRING ( date )
const JSDateToFormattedString = (date: Date, format: string = YYYY_MM_DD) => {
  return DateTime.fromJSDate(date).toFormat(format);
};

// JS DATE -> ISO String
const JSDateToISOString = (date: Date) => {
  return DateTime.fromJSDate(date).toISO();
};

// JS DATE -> TIMESTAMP
const JSDateToTimeStamp = (date: Date) => {
  return Math.floor(DateTime.fromJSDate(date).toSeconds());
};

// dateString -> JS DATE INSTANCE
const GetJSDateOfString = (dateString: string) => {
  return new Date(DateTime.fromISO(dateString).toISODate());
};

// dateString -> JS DATE INSTANCE WITH HOURS 0, MINUTES 0, SECONDS 0
const GetJSDateAtZeroHoursOfString = (dateString: string) => {
  const dateTimeInstance = DateTime.fromISO(dateString);
  const dateInstance = new Date(
    dateTimeInstance.year,
    dateTimeInstance.month - 1,
    dateTimeInstance.day
  );
  dateInstance.setHours(0);
  dateInstance.setMinutes(0);
  dateInstance.setSeconds(0);

  return dateInstance;
};

// STRING ( 2023-11-12 10:13 ) -> timestamp
const GetTimeStampOfDateAsString = (dateAsString: string, format: string = YYYY_MM_DD_HH_MM_A) => {
  return Math.floor(DateTime.fromFormat(`${dateAsString}`, format).valueOf() / 1000);
};

// string -> luxon datetime
const GetDateTimeInstanceOfString = (dateTime: string) => {
  return DateTime.fromISO(dateTime);
};

// luxon date -> add no of days
const AddNoOfDaysToDate = (dateTime: DateTime, days: number) => {
  return dateTime.plus({ day: days });
};

// luxon date -> add no of days
const SubtractNoOfDaysToDate = (dateTime: DateTime, days: number) => {
  return dateTime.minus({ day: days });
};

// date string -> get timestamp of start of day string
const GetStartOfDayInSeconds = (dateTime: string) => {
  return DateTime.fromISO(dateTime).startOf('day').toSeconds();
};

// date string -> get timestamp of end of day string
const GetEndOfDayInSeconds = (dateTime: string) => {
  return DateTime.fromISO(dateTime).endOf('day').toSeconds();
};

// format simple
const getFormattedStringOfDateTime = (dateTime: DateTime, format: string = YYYY_MM_DD) => {
  return dateTime.toFormat(format);
};

const getDateTime = (format = YYYY_MM_DD) => {
  return DateTime.now().toFormat(format);
};

const getFormattedDateFromIsoDate: getFormattedDateFromIsoDateType = (
  date: string,
  format: string,
  timeZone = DEFAULT_TIME_ZONE
) => {
  return DateTime.fromISO(date)
    .setZone(shouldUseLocalTimezone() ? 'local' : timeZone)
    .toFormat(format);
};

/**
 * tsToJSDateWithTimezonedProperties - timestamp to js date with timezoned properties
 * @param timeStamp - timestamp in seconds ( previously timezoned )
 * @param timeZone  - timezone string
 * @returns - new Date() instance with timezoned properties
 *
 */
const tsToJSDateWithTimezonedProperties = (timeStamp: number, timeZone = DEFAULT_TIME_ZONE) => {
  const dateTimeInstance = DateTime.fromSeconds(timeStamp).setZone(
    shouldUseLocalTimezone() ? 'local' : timeZone
  );

  const year = dateTimeInstance.year;
  const month = dateTimeInstance.month - 1; // month is 0 indexed
  const day = dateTimeInstance.day;
  const hour = dateTimeInstance.hour;
  const minute = dateTimeInstance.minute;
  const second = dateTimeInstance.second;

  const newJSDate = new Date(year, month, day, hour, minute, second);
  return newJSDate;
};

/**
 *
 * @param dateInstance js date instance
 * @param timeZone time zone (required)
 * @returns  timestamp in seconds which is converted to time zone for selected date, hour, minute and seconds
 */
const jsDateToTimeZonedTimeStamp = (dateInstance: Date, timeZone = DEFAULT_TIME_ZONE) => {
  const hour = dateInstance.getHours();
  const minute = dateInstance.getMinutes();
  const seconds = dateInstance.getSeconds();

  const timestamp = DateTime.fromISO(FormatDateTime(dateInstance, 'yyyy-MM-dd', null), {
    zone: shouldUseLocalTimezone() ? undefined : timeZone,
  })
    .set({
      hour: hour,
      minute: minute,
      second: seconds,
    })
    .toSeconds();

  return timestamp;
};

/**
 * convertSecondsToTime
 * @param {string} secondsFromMidnight - seconds from midnight
 * example if it is 3600 then it is 1 hour from midnight ( 1:00 AM )
 * @returns {Date} - returns Date instance with hours and minutes set from seconds
 * Date is in Local timezone
 */

const secondsFromMidNightToLocalDate = (secondsFromMidnight: string | null | undefined) => {
  const tempDate = new Date();
  tempDate.setHours(0);
  tempDate.setMinutes(0);

  if (!secondsFromMidnight) return null;
  if (!parseInt(secondsFromMidnight)) return null;
  const totalSeconds = parseInt(secondsFromMidnight, 10);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  tempDate.setHours(hours);
  tempDate.setMinutes(minutes);
  return tempDate;
};

/**
 * convertLocalDateTimeToSeconds
 * @param {Date} dateTime - Date instance
 * @returns {string} - returns seconds from midnight
 * example if it is 1:00 AM then it is 3600 seconds from midnight
 */
const localDateToSecondsFromMidNight = (dateTime: Date | null | undefined) => {
  if (!dateTime) return null;

  const hours = dateTime.getHours();
  const minutes = dateTime.getMinutes();
  const totalSeconds = hours * 3600 + minutes * 60;
  return totalSeconds.toString();
};

/**
 *
 * @param day - day of the week
 * @returns - number of the day
 * example - sunday -> 0, monday -> 1, tuesday -> 2, wednesday -> 3, thursday -> 4, friday -> 5, saturday -> 6
 */
const dayOfWeekToNumber = (day: string) => {
  if (!day) return -1;
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  if (!days.includes(day.toLowerCase())) return -1;
  return days.indexOf(day.toLowerCase());
};

/**
 *
 * @param timeStamp - timestamp in seconds
 * @param timeZone - time zone
 * @returns - JS Date instance in local timezone
 */
const timeStampToJSDate = (timeStamp: number, timeZone = DEFAULT_TIME_ZONE) => {
  return DateTime.fromSeconds(timeStamp)
    .setZone(shouldUseLocalTimezone() ? 'local' : timeZone)
    .toJSDate();
};

/**
 *
 * @param time - The time you want to check
 * @param startTime - The begining of the range
 * @param endTime - The end of the range
 * @returns - Boolean depending on whether or not the time falls in the range
 */
const isInRange = (time: string, startTime: string, endTime: string): boolean => {
  // Parse the times using Luxon's DateTime object
  const timeDT = DateTime.fromFormat(time, 'HH:mm');
  const startDT = DateTime.fromFormat(startTime, 'HH:mm');
  const endDT = DateTime.fromFormat(endTime, 'HH:mm');

  // If start less than end, assume doesn't go over midnight
  if (startDT < endDT) {
    return timeDT >= startDT && timeDT <= endDT;
  } else {
    // Otherwise, assume goes over midnight
    return timeDT >= startDT || timeDT <= endDT;
  }
};

/**
 *
 * @param timeZone - time zone
 * @returns - timestamp of today in seconds
 */
const GetTodayTimestamp = (timeZone = DEFAULT_TIME_ZONE) => {
  return DateTime.now()
    .setZone(shouldUseLocalTimezone() ? 'local' : timeZone)
    .toUnixInteger();
};

// Export all functions at once for easier maintenance and better readability
export {
  GetToday, // Get today's date in specified format and timezone
  FormatTimeStamp, // Format a timestamp in specified format and timezone
  compareDates, // Compare two dates based on specified criteria
  FormatDateTime, // Format a DateTime object in specified format and timezone
  OperationInDate, // Perform an operation (add/subtract) on a date
  DatePickerOperationInDate, // Perform an operation (add/subtract) on a date on datePicker
  DatesDiff, // Get the difference between two dates
  FormatShortDate, // Format a date in short day format
  GetStartWeekDate, // Get the start date of the week for a given date
  GetStartEndDayUnix, // Get the start and end of day Unix timestamps for a given date
  GetStartEndDayUnixFromFormat, // Get the start and end of day Unix timestamps for a given date string
  formatTimeStamp, // Format a timestamp in specified locale string
  isOlderThanOneMonth, // Check if a timestamp is older than one month
  convertToTimezone, // Convert a date to a specified timezone
  ISODateLocalToUTCTimestamp, // Convert an ISO date string to a UTC timestamp
  formatISODate, // Format a date string to ISO standard extended format
  JSDateToFormattedStringWTz, // Convert JS Date to formatted string with timezone
  JSDateToFormattedString, // Convert JS Date to formatted string
  JSDateToISOString, // Convert JS Date to ISO string
  JSDateToTimeStamp, // Convert JS Date to timestamp
  GetJSDateOfString, // Convert date string to JS Date instance
  GetJSDateAtZeroHoursOfString, // Convert date string to JS Date instance with hours, minutes, and seconds set to 0
  GetTimeStampOfDateAsString, // Convert date string to timestamp
  GetDateTimeInstanceOfString, // Convert date string to Luxon DateTime instance
  AddNoOfDaysToDate, // Add number of days to a Luxon DateTime instance
  SubtractNoOfDaysToDate, // Subtract number of days from a Luxon DateTime instance
  GetStartOfDayInSeconds, // Get the start of day timestamp in seconds for a date string
  GetEndOfDayInSeconds, // Get the end of day timestamp in seconds for a date string
  getFormattedStringOfDateTime, // Get formatted string of a Luxon DateTime instance
  getDateTime, // Get current date and time in specified format
  getFormattedDateFromIsoDate, // Get formatted date string from ISO date string
  tsToJSDateWithTimezonedProperties, // Convert timestamp to JS Date with timezoned properties
  jsDateToTimeZonedTimeStamp, // Convert JS Date to timestamp with timezone
  secondsFromMidNightToLocalDate, // Convert seconds from midnight to local Date instance
  localDateToSecondsFromMidNight, // Convert local Date instance to seconds from midnight
  dayOfWeekToNumber, // Convert day of the week to number
  timeStampToJSDate, // Convert timestamp to JS Date instance
  isInRange, // Check if a time falls within a specified range
  GetTodayTimestamp, // Get today's timestamp in seconds
  getFormattedTime, // Get formatted time from a timestamp
};
